import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Mic,
  FileText,
  Clock,
  TrendingUp,
  Download,
  Play,
  RotateCcw,
} from "lucide-react";
import { SearchAndFilter } from "../../components/shared";
import { LoadingState } from "@/components/ui/loading-state";
import { PageHeader } from "@/components/ui/page-header";
import { Button, Card } from "../../components/ui";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@/components/ui/table";
import { PageIntent } from "../../docs/ux/page-intents";

// Page Intent: CONFIGURATION - table-first, no stats cards, no charts
const pageIntent: PageIntent = "CONFIGURATION";

interface CallRecording {
  id: string;
  callId: string;
  agentName: string;
  customerName: string;
  duration: number;
  fileSize: number;
  status: "RECORDED" | "PROCESSING" | "TRANSCRIBED" | "FAILED";
  quality: "EXCELLENT" | "GOOD" | "FAIR" | "POOR";
  tags: string[];
  createdAt: string;
  updatedAt: string;
  metadata: {
    queue: string;
    callType: string;
    satisfaction: number;
    keywords: string[];
  };
  transcript?: string;
  sentiment: "POSITIVE" | "NEUTRAL" | "NEGATIVE";
  complianceFlags: string[];
}

const CallRecordingPage: React.FC = () => {
  const { t } = useTranslation();
  const [recordings, setRecordings] = useState<CallRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedQuality, setSelectedQuality] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Mock data - replace with actual API calls
  useEffect(() => {
    const mockRecordings: CallRecording[] = [
      {
        id: "1",
        callId: "CALL-001-2024",
        agentName: "Sarah Johnson",
        customerName: "Ahmed Hassan",
        duration: 325,
        fileSize: 2.4,
        status: "TRANSCRIBED",
        quality: "EXCELLENT",
        tags: ["support", "technical"],
        createdAt: "2024-01-15T10:30:00Z",
        updatedAt: "2024-01-15T11:00:00Z",
        metadata: {
          queue: "Technical Support",
          callType: "Inbound",
          satisfaction: 5,
          keywords: ["login", "password", "reset"],
        },
        transcript: "Customer called regarding login issues...",
        sentiment: "POSITIVE",
        complianceFlags: [],
      },
      {
        id: "2",
        callId: "CALL-002-2024",
        agentName: "Mike Chen",
        customerName: "Fatima Ali",
        duration: 180,
        fileSize: 1.8,
        status: "TRANSCRIBED",
        quality: "GOOD",
        tags: ["billing", "payment"],
        createdAt: "2024-01-15T09:15:00Z",
        updatedAt: "2024-01-15T09:45:00Z",
        metadata: {
          queue: "Billing Support",
          callType: "Inbound",
          satisfaction: 4,
          keywords: ["invoice", "payment", "refund"],
        },
        transcript: "Customer inquired about invoice payment...",
        sentiment: "NEUTRAL",
        complianceFlags: ["payment_mentioned"],
      },
      {
        id: "3",
        callId: "CALL-003-2024",
        agentName: "Lisa Rodriguez",
        customerName: "Omar Khalil",
        duration: 420,
        fileSize: 3.2,
        status: "PROCESSING",
        quality: "FAIR",
        tags: ["sales", "inquiry"],
        createdAt: "2024-01-15T08:45:00Z",
        updatedAt: "2024-01-15T08:45:00Z",
        metadata: {
          queue: "Sales",
          callType: "Inbound",
          satisfaction: 0,
          keywords: ["pricing", "features", "demo"],
        },
        sentiment: "POSITIVE",
        complianceFlags: [],
      },
      {
        id: "4",
        callId: "CALL-004-2024",
        agentName: "Alex Thompson",
        customerName: "Nour El-Din",
        duration: 95,
        fileSize: 0.8,
        status: "RECORDED",
        quality: "POOR",
        tags: ["complaint", "escalation"],
        createdAt: "2024-01-15T07:30:00Z",
        updatedAt: "2024-01-15T07:30:00Z",
        metadata: {
          queue: "Customer Service",
          callType: "Inbound",
          satisfaction: 2,
          keywords: ["complaint", "issue", "unhappy"],
        },
        sentiment: "NEGATIVE",
        complianceFlags: ["complaint_escalation"],
      },
      {
        id: "5",
        callId: "CALL-005-2024",
        agentName: "David Wilson",
        customerName: "Mariam Ahmed",
        duration: 210,
        fileSize: 1.6,
        status: "FAILED",
        quality: "POOR",
        tags: ["support", "technical"],
        createdAt: "2024-01-15T06:15:00Z",
        updatedAt: "2024-01-15T06:20:00Z",
        metadata: {
          queue: "Technical Support",
          callType: "Inbound",
          satisfaction: 0,
          keywords: [],
        },
        sentiment: "NEUTRAL",
        complianceFlags: ["recording_failed"],
      },
    ];

    setRecordings(mockRecordings);
    setLoading(false);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "TRANSCRIBED":
        return "success";
      case "PROCESSING":
        return "warning";
      case "RECORDED":
        return "info";
      case "FAILED":
        return "failure";
      default:
        return "gray";
    }
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case "EXCELLENT":
        return "success";
      case "GOOD":
        return "info";
      case "FAIR":
        return "warning";
      case "POOR":
        return "failure";
      default:
        return "gray";
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "POSITIVE":
        return "success";
      case "NEUTRAL":
        return "info";
      case "NEGATIVE":
        return "failure";
      default:
        return "gray";
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const filteredRecordings = recordings.filter(recording => {
    const matchesStatus =
      selectedStatus === "all" || recording.status === selectedStatus;
    const matchesQuality =
      selectedQuality === "all" || recording.quality === selectedQuality;
    const matchesSearch =
      recording.callId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recording.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recording.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recording.tags.some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );

    return matchesStatus && matchesQuality && matchesSearch;
  });

  if (loading) {
    return <LoadingState />;
  }

  const filterOptions = [
    {
      label: t("all-statuses"),
      value: "all",
    },
    {
      label: t("recorded"),
      value: "RECORDED",
    },
    {
      label: t("processing"),
      value: "PROCESSING",
    },
    {
      label: t("transcribed"),
      value: "TRANSCRIBED",
    },
    {
      label: t("failed"),
      value: "FAILED",
    },
  ];

  const qualityOptions = [
    {
      label: t("all-qualities"),
      value: "all",
    },
    {
      label: t("excellent"),
      value: "EXCELLENT",
    },
    {
      label: t("good"),
      value: "GOOD",
    },
    {
      label: t("fair"),
      value: "FAIR",
    },
    {
      label: t("poor"),
      value: "POOR",
    },
  ];

  return (
    <div className="min-h-full bg-white">
      <PageHeader
        title={t("callRecording")}
        description={t("call-recording-description")}
        icon={Mic}
        iconBgColor="from-blue-500 to-blue-600"
        actions={
          <Button variant="primary" size="sm">
            <Download className="mr-2 h-4 w-4" />
            {t("export-recordings")}
          </Button>
        }
      />

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t("search-for-recording")}
          filters={[
            {
              label: t("status"),
              value: selectedStatus,
              options: filterOptions,
              onChange: setSelectedStatus,
            },
            {
              label: t("quality"),
              value: selectedQuality,
              options: qualityOptions,
              onChange: setSelectedQuality,
            },
          ]}
          resultsCount={filteredRecordings.length}
          totalCount={recordings.length}
          resultsLabel={t("recordings")}
        />

        <Card className="mb-6">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("call-id")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("agent")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("customer")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("duration")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("status")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("quality")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("sentiment")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("satisfaction")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("file-size")}
                </TableHead>
                <TableHead className="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">
                  {t("actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecordings.map(recording => (
                <TableRow key={recording.id}>
                  <TableCell className="px-6 py-4 text-sm font-medium text-gray-900">
                    {recording.callId}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900">
                    {recording.agentName}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900">
                    {recording.customerName}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {formatDuration(recording.duration)}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getStatusColor(recording.status) === "success"
                          ? "bg-green-100 text-green-800"
                          : getStatusColor(recording.status) === "warning"
                            ? "bg-yellow-100 text-yellow-800"
                            : getStatusColor(recording.status) === "info"
                              ? "bg-blue-100 text-blue-800"
                              : getStatusColor(recording.status) === "failure"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(recording.status.toLowerCase())}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getQualityColor(recording.quality) === "success"
                          ? "bg-green-100 text-green-800"
                          : getQualityColor(recording.quality) === "info"
                            ? "bg-blue-100 text-blue-800"
                            : getQualityColor(recording.quality) === "warning"
                              ? "bg-yellow-100 text-yellow-800"
                              : getQualityColor(recording.quality) ===
                                  "failure"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(recording.quality.toLowerCase())}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getSentimentColor(recording.sentiment) === "success"
                          ? "bg-green-100 text-green-800"
                          : getSentimentColor(recording.sentiment) === "info"
                            ? "bg-blue-100 text-blue-800"
                            : getSentimentColor(recording.sentiment) ===
                                "failure"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {t(recording.sentiment.toLowerCase())}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {recording.metadata.satisfaction > 0
                      ? recording.metadata.satisfaction
                      : "-"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-500">
                    {recording.fileSize}MB
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm">
                        <Play className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Download className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <FileText className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {filteredRecordings.length === 0 && (
          <Card className="text-center py-12">
            <Mic className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("no-recordings-found")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("no-recordings-description")}
            </p>
            <Button variant="primary" size="sm">
              <RotateCcw className="mr-2 h-4 w-4" />
              {t("refresh")}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CallRecordingPage;
